import type { IconName } from '@w7/ui'
import type { Role } from '@/data/types'
import { canAccessLogPage, type LogPageId } from '@/features/logs/access'
import type { LegacyProfile } from '@/features/book-management/accessLevel'
import { bookMenuGroups } from '@/features/book-management/menu'

/**
 * The navigation model.
 *
 * The console this replaces had a flat rail of nine unrelated items — Member,
 * Commission, Book Management, Account Statement, All
 * Logs, Change Password, Google Authentication, Logout — mixing a daily
 * workspace with account settings and a sign-out link, and burying the five
 * report screens two levels down under "Account Statement".
 *
 * Here the rail is grouped by the question being asked:
 *   Run the book   — what is happening right now
 *   Member         — who is beneath me and what are they doing
 *   Account Statement — what moved, what is owed, what did I earn
 *   Commission     — Lab commission-report (own group)
 *   Activity       — operational audit
 *   Logs           — Winner All Logs pages (TM / Master only)
 * Settings and sign-out leave the rail entirely and live under the account menu.
 *
 * The owner's six named master actions each map to exactly one destination:
 *   give/take            -> Member List (row action + toolbar), and Credit transfers
 *   create masters/users -> Add to downline / User Access Level
 *   net exposure         -> Book Management (live Net Exposure mode)
   *   user P&L             -> PL Statement
   *   PL statement         -> PL Statement (event rows; Lab /PLStatement)
   *   transfer statement   -> Credit transfers
   *   my P&L               -> My P&L
 */

export interface NavItem {
  id: string
  label: string
  to: string
  icon: IconName
  /** Matches child routes too, e.g. /exposure/market/xyz. */
  match?: string
  /** Extra prefixes that keep this rail item current (e.g. /net-exposure). */
  aliases?: string[]
  description: string
  /** Rendered as a disclosure beneath the item — the legacy menu dropdowns. */
  children?: { id: string; label: string; to: string; icon?: IconName }[]
  /**
   * When set, the item is shown only for these roles. Omitted = every signed-in
   * console role. Logs pages pin Winner All Logs access here and in LogAccessGate.
   */
  roles?: readonly Role[]
  /** Links a Logs nav row to the shared access table. */
  logPageId?: LogPageId
}

export interface NavGroup {
  id: string
  label: string
  /** Optional group toggle icon; defaults to the first item’s icon. */
  icon?: IconName
  items: NavItem[]
}

/** Overview as its own solo rail group (first item after login). */
const OVERVIEW_GROUP: NavGroup = {
  id: 'overview',
  label: 'Overview',
  icon: 'overview',
  items: [
    {
      id: 'overview',
      label: 'Overview',
      to: '/overview',
      icon: 'overview',
      match: '/overview',
      description: 'Position, trend and anything needing attention',
    },
  ],
}

const STATIC_GROUPS: NavGroup[] = [
  {
    id: 'people',
    label: 'Member',
    items: [
      {
        id: 'member-list',
        label: 'Member List',
        to: '/downline',
        icon: 'downline',
        match: '/downline',
        aliases: ['/members'],
        description: 'Direct children, the whole tree, give/take, and Monday’s bulk settle',
      },
      {
        id: 'hierarchy-search',
        label: 'Hierarchy Search',
        to: '/hierarchy-search',
        icon: 'search',
        match: '/hierarchy-search',
        // Lab strips this for Master — only agents who can see above Master need the chain table.
        roles: ['triple-master', 'double-master', 'super-master'],
        description: 'Find anyone in your tree and see Player → Master → SM → DM at a glance',
      },
      {
        id: 'user-access',
        label: 'User Access Level',
        to: '/users',
        icon: 'users',
        match: '/users',
        description: 'Staff console logins under you — Full / View / Restrict per capability',
      },
      {
        id: 'create',
        label: 'Add to downline',
        to: '/create',
        icon: 'plus',
        description: 'Create an agent or a member — one page, everything visible',
      },
      {
        id: 'player-2fa',
        label: 'Sign-in 2FA',
        to: '/player-2fa',
        icon: 'shield',
        match: '/player-2fa',
        description: 'Members (portal) and agents (console) — who has a second factor on',
      },
    ],
  },
  {
    id: 'money',
    label: 'Account Statement',
    items: [
      {
        id: 'my-pnl',
        label: 'My P&L',
        to: '/reports/my-pnl',
        icon: 'pnl',
        description: 'What the book earned you, after shares and commission',
      },
      {
        id: 'pl-statement',
        label: 'PL Statement',
        to: '/reports/pl-statement',
        icon: 'pnl',
        aliases: ['/PLStatement'],
        description: 'Settled markets with Member / Master / SM / DM / TM net — Lab PLStatement',
      },
      {
        id: 'transfers',
        // Lab Access Level label — Master catalog "Transfer Statement".
        label: 'Transfer Statement',
        to: '/reports/transfers',
        icon: 'transfers',
        description: 'Every give and take, with running balances',
      },
      {
        id: 'turnover',
        label: 'Turnover',
        to: '/reports/turnover',
        icon: 'turnover',
        description: 'Matched volume by product, sport and agent',
      },
    ],
  },
  {
    id: 'commission',
    label: 'Commission',
    items: [
      {
        id: 'commission-report',
        label: 'Commission Report',
        to: '/reports/commission/eligibility',
        icon: 'commission',
        match: '/reports/commission',
        description: 'Eligible / Ineligible players by week — Lab commission-report',
      },
    ],
  },
  {
    id: 'trail',
    label: 'Activity',
    items: [
      {
        id: 'activity',
        label: 'Activity',
        to: '/activity',
        icon: 'activity',
        description: 'Who did what, when, from where',
      },
    ],
  },
  {
    id: 'logs',
    label: 'Logs',
    items: [
      {
        id: 'logs-cl-transfers',
        label: 'CL Transfers',
        to: '/logs/cl-transfers',
        icon: 'sliders',
        logPageId: 'cl-transfers',
        roles: ['triple-master', 'super-master', 'master'],
        description: 'Credit-limit changes across the hierarchy',
      },
      {
        id: 'logs-multiple-device-ids',
        label: 'Multiple Device Ids',
        to: '/logs/multiple-device-ids',
        icon: 'users',
        logPageId: 'multiple-device-ids',
        roles: ['triple-master', 'super-master'],
        description: 'Players sharing the same device id',
      },
      {
        id: 'logs-betfair-rates',
        label: 'BetfairRates Logs',
        to: '/logs/betfair-rates',
        icon: 'sport',
        logPageId: 'betfair-rates',
        roles: ['master'],
        description: 'Betfair rate and min-bet changes',
      },
      {
        id: 'logs-invalid-bets',
        label: 'Invalid Bets',
        to: '/logs/invalid-bets',
        icon: 'warning',
        logPageId: 'invalid-bets',
        roles: ['master'],
        description: 'VAR and invalid bet logs',
      },
      {
        id: 'logs-blocked-device-ids',
        label: 'Blocked Device Ids',
        to: '/logs/blocked-device-ids',
        icon: 'lock',
        logPageId: 'blocked-device-ids',
        roles: ['master'],
        description: 'Blocked device ids and linked players',
      },
      {
        id: 'logs-blocked-ip',
        label: 'Blocked IP',
        to: '/logs/blocked-ip',
        icon: 'lock',
        logPageId: 'blocked-ip',
        roles: ['master'],
        description: 'Blocked IPs, comments, and players',
      },
      {
        id: 'logs-login-from-blocked-ip',
        label: 'Login From Blocked IP',
        to: '/logs/login-from-blocked-ip',
        icon: 'shield',
        logPageId: 'login-from-blocked-ip',
        roles: ['master'],
        description: 'Sign-ins that came from a blocked IP',
      },
    ],
  },
]

/**
 * The rail for one operator.
 *
 * Book Management is a dropdown whose items depend on the access level, so the
 * rail cannot be a single frozen constant any more.
 */
export function buildNav(profile: LegacyProfile | null): NavGroup[] {
  const books = bookMenuGroups(profile).map(
    (group): NavItem => ({
      id: group.id,
      label: group.label,
      to: group.to,
      icon: group.icon,
      match: group.match,
      aliases: group.aliases,
      description: group.description,
      children: group.children,
    }),
  )

  return [
    OVERVIEW_GROUP,
    { id: 'run', label: 'Book Management', icon: 'net-exposure', items: books },
    ...STATIC_GROUPS,
  ]
}

/** Rail group that owns `pathname` (same match rules as the rail). */
export function activeNavGroupId(
  groups: NavGroup[],
  pathname: string,
): string | null {
  for (const group of groups) {
    for (const item of group.items) {
      if (isNavItemActive(pathname, item)) return group.id
    }
  }
  return null
}

/**
 * Full rail, used by the command palette so a page an operator can open by URL
 * is also findable by name. Route gates decide what actually opens: every
 * admitted tier gets the same book pages, so this is the same list they see.
 */
export const NAV: NavGroup[] = buildNav({ accessLevel: 'Master' })

export const ALL_NAV_ITEMS: NavItem[] = NAV.flatMap((group) => group.items)

function itemVisibleToRole(item: NavItem, role: Role | undefined): boolean {
  if (item.logPageId) return canAccessLogPage(role, item.logPageId)
  if (!item.roles) return true
  return Boolean(role && item.roles.includes(role))
}

/**
 * Role filter over a built rail — drops empty groups (Logs for Super Master).
 * `keepHierarchySearch`: Access Level staff under Master with hierarchy-search.view
 * still need the page; Lab only strips it for Master *owners*.
 */
export function filterNavForRole(
  groups: NavGroup[],
  role: Role | undefined,
  opts?: { keepHierarchySearch?: boolean },
): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.id === 'hierarchy-search' && opts?.keepHierarchySearch) return true
        return itemVisibleToRole(item, role)
      }),
    }))
    .filter((group) => group.items.length > 0)
}

/** Rail + Jump-to catalogue for the signed-in role. Empty groups are dropped. */
export function navForRole(role: Role | undefined): NavGroup[] {
  return filterNavForRole(NAV, role)
}

export function navItemsForRole(role: Role | undefined): NavItem[] {
  return navForRole(role).flatMap((group) => group.items)
}

export function isNavItemActive(pathname: string, item: NavItem): boolean {
  if (item.to === '/' && !item.match) return pathname === '/'
  const prefixes = [item.match ?? item.to, ...(item.aliases ?? [])]
  return prefixes.some((prefix) => {
    if (prefix === '/') return pathname === '/'
    return pathname === prefix || pathname.startsWith(`${prefix}/`)
  })
}
